'use client';

import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { useResizableColumn } from '@/shared/lib/hooks/use-resizable-column';

/** Apple's `LayoutMode.settingsPanelWidth` — also the narrowest the column goes. */
const MIN_WIDTH = 312;
/** Past this the inspector stops being a panel and starts being a second page. */
const MAX_WIDTH = 720;
const STORAGE_KEY = 'inspector-panel-width';

/**
 * Default width for the window we are in, used until the user drags the edge.
 * A laptop keeps Apple's 312; a desktop monitor has room the laptop does not,
 * and at 312 the rows there read as a thin strip beside a huge canvas.
 * Mirrored in Apple's `LayoutMode.defaultSettingsPanelWidth`.
 */
function defaultWidthFor(windowWidth: number): number {
  if (windowWidth >= 1920) return 460;
  if (windowWidth >= 1600) return 400;
  return MIN_WIDTH;
}

/**
 * The trailing inspector column (render settings, estimate, album), resizable
 * by dragging its inner edge.
 *
 * The grip has no resting appearance: no line, no dots, no hover fill. It is an
 * 8px band in the gap beside the column that only changes the cursor, so the
 * layout reads exactly as it did before while still yielding to anyone who pulls
 * at it; a hairline appears along the edge for as long as the drag lasts, which
 * on a tablet is the only feedback a finger gets. Double-click returns the
 * column to the width this window would have chosen on its own.
 */
export function InspectorColumn({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const { width, ref, isDragging, gripHandlers } = useResizableColumn({
    storageKey: STORAGE_KEY,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    defaultWidthFor: useCallback(defaultWidthFor, []),
    edge: 'leading',
  });

  return (
    // The clipping `aside` is nested inside an unclipped shell: the grip has to
    // live OUTSIDE the column's `overflow-hidden`, or the half of it that hangs
    // into the gap is clipped away and stops receiving the pointer — the column
    // looked resizable and was not.
    <div
      ref={ref}
      style={width === null ? undefined : { width }}
      className={cn(
        'relative flex-shrink-0 h-full me-3',
        // Only until the stored width lands — after that the inline style wins.
        width === null &&
          'w-[312px] min-[1600px]:w-[400px] min-[1920px]:w-[460px]',
        className
      )}
    >
      <aside className="h-full w-full overflow-hidden flex flex-col">
        {children}
      </aside>

      {/* The edge only shows itself while it is actually being moved. */}
      <div
        aria-hidden
        className={cn(
          'absolute inset-y-0 start-0 w-0.5 z-[49] pointer-events-none transition-opacity duration-150 bg-primary',
          isDragging ? 'opacity-70' : 'opacity-0'
        )}
      />
      {/* The band sits in the gap BESIDE the column, not on top of its first
          8px: a drill-down sheet covers the column edge to edge (`NestedSheet`,
          z 40-48), and a band laid over it would be buried by it. Out in the
          gap it is clear of the sheets, so the width can be pulled while a
          sub-panel is open — which is exactly where a long value wants the
          room. z-49 keeps it above them and below the modal layer (dialogs at
          z-50, portaled to <body>).
          Pointer-only by design, so it carries no accessible name: there is
          nothing here for a keyboard or a screen reader to operate, and the
          panel is fully usable at whatever width it currently has. */}
      <div
        aria-hidden
        {...gripHandlers}
        className="absolute inset-y-0 start-0 -ms-2 w-2 z-[49] cursor-col-resize touch-none"
      />
    </div>
  );
}
